import React, { useEffect, useRef } from 'react'
import Editor from '@toast-ui/editor'
import '@toast-ui/editor/dist/toastui-editor.css'
import '@toast-ui/editor/dist/i18n/ko-kr'
import './markdown-editor.css'

/**
 * MarkdownEditor — Toast UI Editor(vanilla) 기반 WYSIWYG 에디터.
 *
 * 화면에는 서식이 적용된 모습 그대로 보이고, 값은 markdown 문자열로 입출력한다.
 * 일반 사용자가 markdown 문법을 몰라도 쓸 수 있도록 WYSIWYG 단일 모드로 고정하고
 * (markdown 모드 전환 탭 숨김), 한국어 UI를 사용한다.
 *
 * 공식 React 래퍼(@toast-ui/react-editor)는 react 17 peer 의존이라 React 19 와
 * 충돌하므로 vanilla 패키지를 직접 감쌌다.
 */
interface MarkdownEditorProps {
  /** markdown 원문 */
  value: string
  onChange: (markdown: string) => void
  /** CSS height (예: '240px') */
  height?: string
  placeholder?: string
  disabled?: boolean
}

const MarkdownEditor: React.FC<MarkdownEditorProps> = ({
  value,
  onChange,
  height = '240px',
  placeholder,
  disabled = false,
}) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const editorRef = useRef<Editor | null>(null)

  // change 이벤트 핸들러가 항상 최신 onChange 를 보도록 ref 로 우회 (재구독 방지)
  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange

  // 에디터가 마지막으로 내보낸 값 — 외부 value 변경(AI 적용 등)과 자체 입력을 구분
  const lastValueRef = useRef(value)

  // 프로그래matic 반영(setMarkdown) 중 발생하는 change 이벤트 무시 플래그.
  // Toast UI Editor는 WYSIWYG 모드에서 setMarkdown 호출 시 내부적으로 문서 모델을 통째로
  // 교체(ProseMirror transaction dispatch)하는데, 이 과정에서 'change' 이벤트가 동기적으로
  // 재발생한다. 이걸 그대로 흘려보내면 getMarkdown()이 재직렬화한 값(예: "1." → "1\.",
  // "-" → "*")이 onChange를 통해 부모 state를 덮어써 원본 값이 오염된다.
  const isSyncingRef = useRef(false)

  // 최초 마운트 시 에디터 생성 (StrictMode 이중 마운트는 destroy 로 정리됨)
  useEffect(() => {
    if (!containerRef.current) return

    const editor = new Editor({
      el: containerRef.current,
      height,
      initialEditType: 'wysiwyg',
      hideModeSwitch: true,
      usageStatistics: false,
      language: 'ko-KR',
      placeholder,
      initialValue: lastValueRef.current || '',
      autofocus: false,
      toolbarItems: [
        ['heading', 'bold', 'italic'],
        ['ul', 'ol'],
        ['table', 'link'],
      ],
    })

    editor.on('change', () => {
      if (isSyncingRef.current) return
      const markdown = editor.getMarkdown()
      lastValueRef.current = markdown
      onChangeRef.current(markdown)
    })

    editorRef.current = editor
    return () => {
      editorRef.current = null
      editor.destroy()
    }
    // height/placeholder 변경으로 에디터를 재생성하지 않는다 (마운트 시 1회)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // 외부에서 value 가 바뀐 경우(AI 생성 값 적용, 패널 재오픈 등)에만 에디터에 반영
  useEffect(() => {
    const editor = editorRef.current
    if (!editor) return
    if (value !== lastValueRef.current) {
      lastValueRef.current = value
      isSyncingRef.current = true
      editor.setMarkdown(value || '', false)
      isSyncingRef.current = false
    }
  }, [value])

  return (
    <div
      className={`dot-markdown-editor${disabled ? ' dot-markdown-editor--disabled' : ''}`}
      ref={containerRef}
    />
  )
}

export default MarkdownEditor
